"use client";

import { useContext, useMemo } from "react";
import {
  FilterStableContext,
  FilterStateContext,
  type FilterStableValue,
  type FilterStateValue,
} from "./contexts";
import type { FilterContextValue } from "./types";
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";

/**
 * Access the STABLE half of FilterContext — `{ actions, helpers }`.
 *
 * Prefer this in any component that only dispatches (filter buttons, row
 * context menus, table rows). Its identity does not change when filter state
 * changes, so such a consumer is not re-rendered by a keystroke in table search.
 *
 * Throws if used outside of FilterContextProvider.
 */
export function useFilterStable(): FilterStableValue {
  const context = useContext(FilterStableContext);
  if (!context) {
    throw new Error("useFilterStable must be used within FilterContextProvider");
  }
  return context;
}

/** The 11 filter actions, referentially stable for the life of the provider. */
export function useFilterActions(): FilterContextValue["actions"] {
  return useFilterStable().actions;
}

/** The three GraphQL filter helpers, referentially stable unless config changes. */
export function useFilterHelpers(): FilterContextValue["helpers"] {
  return useFilterStable().helpers;
}

/**
 * Access the VOLATILE half of FilterContext — `{ filters, uiState, computed }`.
 * Consumers of this hook re-render on every filter/search/UI state change.
 *
 * Throws if used outside of FilterContextProvider.
 */
export function useFilterState(): FilterStateValue {
  const context = useContext(FilterStateContext);
  if (!context) {
    throw new Error("useFilterState must be used within FilterContextProvider");
  }
  return context;
}

/**
 * Compatibility hook: the full FilterContext value, composed from both halves.
 *
 * Equivalent to the pre-split hook, so existing call sites are unchanged. It
 * subscribes to BOTH contexts, so it re-renders on every state change — reach
 * for `useFilterActions` / `useFilterHelpers` / `useFilterState` instead when a
 * component only needs one half.
 *
 * Throws error if used outside of provider.
 */
export function useFilterContext<T extends BaseEntity = BaseEntity>(): FilterContextValue<T> {
  // Every hook runs BEFORE the missing-provider throw. Several call sites wrap
  // this hook in try/catch and keep rendering when it throws
  // (`UniversalEntityPage/hooks/useContextHooks.ts`), so the hook count must not
  // depend on whether a provider is mounted.
  const stable = useContext(FilterStableContext);
  const state = useContext(FilterStateContext);

  const value = useMemo<FilterContextValue<T> | undefined>(() => {
    if (!stable || !state) return undefined;
    return {
      filters: state.filters,
      uiState: state.uiState,
      actions: stable.actions,
      computed: state.computed,
      helpers: stable.helpers,
    };
  }, [stable, state]);

  if (!value) {
    throw new Error("useFilterContext must be used within FilterContextProvider");
  }
  return value;
}
